import { useMemo, useState } from 'react'
import { v4 as uuidV4 } from 'uuid'
import type { Moment } from 'moment'

import { DATE_SEARCH_OPTION_IDS, type FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'
import { useTypeaheadStreaming } from 'hooks/Search/useTypeaheadStreaming'
import useOfferList from 'hooks/Offers/useOfferList'
import useOffers from 'hooks/Offers/useOffers'
import { isBedbankOffer, isLEHotel } from 'lib/offer/offerTypes'
import { allRoomsOccupantsFitRoomCapacity } from 'lib/checkout/cartReservationUtils'

interface UseCarouselOfferSearchArgs {
  searchQuery?: string
  rooms: Array<App.Occupants>
  filterActive: boolean
  dateMode: DATE_SEARCH_OPTION_IDS
  checkIn?: Moment
  checkOut?: Moment
  flexibleMonths: string
  flexibleNights?: FLEXIBLE_DURATION_RANGE
  isMessageStreaming: boolean
  originalOfferIds: Array<string>
}

export default function useCarouselOfferSearch({
  searchQuery,
  rooms,
  filterActive,
  dateMode,
  checkIn,
  checkOut,
  flexibleMonths,
  flexibleNights,
  isMessageStreaming,
  originalOfferIds,
}: UseCarouselOfferSearchArgs) {
  const [typeaheadId] = useState(() => uuidV4())
  const canSearch = !!searchQuery

  const { streamingData } = useTypeaheadStreaming(searchQuery || '', typeaheadId, {
    enabled: canSearch,
    debounceMs: 0,
    temporaryDoNotExtract: false,
  })

  const extractedLocation = streamingData?.location?.data
  const extractedPlaceIds = extractedLocation && 'placeIds' in extractedLocation
    ? extractedLocation.placeIds
    : undefined

  const typeaheadResolved = canSearch && !!streamingData && !streamingData.fetching

  const nlsFilters = useMemo<App.OfferListFilters | undefined>(() => {
    if (!filterActive || !extractedPlaceIds?.length) return undefined

    const filters: App.OfferListFilters = {
      destinationId: extractedPlaceIds[0],
      destinationIds: extractedPlaceIds,
      rooms,
      offerTypes: [OFFER_TYPE_HOTEL, OFFER_TYPE_BED_BANK],
      limit: 10,
    }

    if (dateMode === DATE_SEARCH_OPTION_IDS.SPECIFIC && checkIn && checkOut) {
      filters.checkIn = checkIn.format(ISO_DATE_FORMAT)
      filters.checkOut = checkOut.format(ISO_DATE_FORMAT)
    } else if (dateMode === DATE_SEARCH_OPTION_IDS.FLEXIBLE) {
      if (flexibleMonths) filters.flexibleMonths = flexibleMonths
      if (flexibleNights) filters.flexibleNights = flexibleNights
    }

    return filters
  }, [checkIn, checkOut, dateMode, extractedPlaceIds, filterActive, flexibleMonths, flexibleNights, rooms])

  const nlsOfferList = useOfferList(nlsFilters, {
    typeaheadId,
    filterSoldOut: true,
    clientCheckAvailability: true,
    disabled: !nlsFilters,
  })

  const activeOfferIds = filterActive
    ? nlsOfferList.offerIds
    : originalOfferIds

  const [allLoadedOffers, offersFetching] = useOffers<App.AnyOffer>(activeOfferIds)

  // NLS doesn't enforce room capacity constraints, so we verify each offer
  // can accommodate the requested occupancy client-side.
  const offers = useMemo(() => {
    if (!filterActive) return allLoadedOffers
    return allLoadedOffers.filter(offer => {
      if (isLEHotel(offer)) {
        if (!offer.packages?.length) return true
        return rooms.every((room: App.Occupants) =>
          offer.packages.some(pkg =>
            'roomRate' in pkg && pkg.roomRate?.capacities?.length
              ? allRoomsOccupantsFitRoomCapacity(room, pkg.roomRate.capacities, offer)
              : true,
          ),
        )
      }
      if (isBedbankOffer(offer)) {
        if (!offer.packages?.length) return true
        return rooms.every((room: App.Occupants) =>
          offer.packages.some(pkg =>
            pkg.capacities?.combinations?.length
              ? allRoomsOccupantsFitRoomCapacity(room, pkg.capacities.combinations, offer as App.BedbankOffer)
              : true,
          ),
        )
      }
      return true
    })
  }, [allLoadedOffers, rooms, filterActive])

  const filteredOfferIds = useMemo(
    () => offers.map(offer => offer.id),
    [offers],
  )

  // When the typeahead finishes but can't resolve a location, treat as loaded
  // (empty) rather than showing loading skeletons indefinitely.
  const isFullyLoaded = filterActive
    ? (typeaheadResolved && !extractedPlaceIds?.length)
    || (!!nlsFilters && !nlsOfferList.fetching && !offersFetching && activeOfferIds.length === allLoadedOffers.length)
    : (!isMessageStreaming && !offersFetching && activeOfferIds.length > 0 && activeOfferIds.length === allLoadedOffers.length)

  const showLoadingCards = !isFullyLoaded
  const showEmptyState = isFullyLoaded && filteredOfferIds.length === 0 && filterActive

  return {
    offers,
    filteredOfferIds,
    offersFetching,
    isFullyLoaded,
    showLoadingCards,
    showEmptyState,
    typeaheadId,
  }
}
